import type { UIEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { useTheme } from 'styled-components';

import { TrackingInteractiveQuestionType } from '@genially/contracts';
import { useResizeObserver } from '@genially/document-components/build/hooks/useResizeObserver';
import type { SurveyInteractiveQuestionItemData } from '@genially/document-model';

import { calculateSurveyPercentages } from '../../../../utils/calculateSurveyPercentages';
import { useSidebarSummaryWithContentContext } from '../../SidebarSummaryWithContent.context';
import { useSurveySidebarData } from './hooks/useSurveySidebarData';
import {
  ChildrenMansoryWrapper,
  MasonryWrapper,
  QuestionTitleBasicParagraph,
  QuestionTitleBigParagraph,
  QuestionTitleWrapper,
  StyledBiggerSubtitle,
  StyledBigSubtitle,
} from '../../../../SidebarSummary.styled';
import {
  DataRowTextWrapper,
  DataRowWrapper,
  DataWrapper,
  HeaderWrapper,
  ScrollWrapper,
  StyledBasicParagraph,
  SurveyPercentage,
  SurveyPercentageLabel,
  SurveyPercentageLabelWrapper,
  Wrapper,
} from './SurveySummaryData.styled';

interface SurveySummaryDataProps {
  isFullWidthEnabled?: boolean;
}

const GRADIENT_THRESHOLD = 10;

export const SurveySummaryData = ({ isFullWidthEnabled }: SurveySummaryDataProps) => {
  const theme = useTheme();

  const {
    selectedQuestion,
    config: { getTranslation },
    selectedQuestionAnswer,
  } = useSidebarSummaryWithContentContext<SurveyInteractiveQuestionItemData>();

  const [hasScrollTop, setHasScrollTop] = useState(false);
  const [hasScrollBottom, setHasScrollBottom] = useState(true);

  const [surveyData, setSurveyData] = useState<
    { id: string; text: string; percentage: number; total: number }[]
  >([]);

  const { aggregatedData, totalData } = useSurveySidebarData();

  const setGradients = (element: HTMLDivElement) => {
    setHasScrollTop(element.scrollTop >= GRADIENT_THRESHOLD);
    setHasScrollBottom(
      element.scrollHeight - element.scrollTop - element.clientHeight >=
        GRADIENT_THRESHOLD,
    );
  };

  const scrollRef = useResizeObserver(() => {
    if (scrollRef.current) {
      setGradients(scrollRef.current);
    }
  });

  useEffect(() => {
    if (scrollRef.current) {
      setGradients(scrollRef.current);
    }
  }, [scrollRef]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;

    setGradients(element);
  };

  const calculateSurveyData = useCallback(
    (question: SurveyInteractiveQuestionItemData) => {
      const percentages = calculateSurveyPercentages({
        result: aggregatedData,
        answerIds: question.Answers.map(answer => answer.Id) ?? [],
      });

      const percentagesPerAnswers = question.Answers.map(answer => {
        const data = percentages.find(({ id }) => id === answer.Id);

        return {
          id: answer.Id,
          text: answer.Text,
          percentage: data?.percentage ?? 0,
          total: data?.total ?? 0,
        };
      }).sort((a, b) => b.percentage - a.percentage);

      setSurveyData(percentagesPerAnswers);
    },
    [aggregatedData],
  );

  useEffect(() => {
    if (selectedQuestion) {
      calculateSurveyData(selectedQuestion);
    }
  }, [calculateSurveyData, selectedQuestion]);

  const QuestionTitleComponent = isFullWidthEnabled
    ? QuestionTitleBasicParagraph
    : QuestionTitleBigParagraph;

  const TotalParticipantsComponent = isFullWidthEnabled
    ? StyledBiggerSubtitle
    : StyledBigSubtitle;

  const getData = useCallback(() => {
    return surveyData.map(({ id, text, percentage, total }, index) => (
      <DataRowWrapper
        key={id}
        $isSelected={Boolean(
          selectedQuestionAnswer &&
            selectedQuestionAnswer.type === TrackingInteractiveQuestionType.Survey &&
            selectedQuestionAnswer.answerIds.includes(id),
        )}
      >
        <DataRowTextWrapper>
          <StyledBasicParagraph>{text}</StyledBasicParagraph>
        </DataRowTextWrapper>

        <SurveyPercentage $index={index} $percentage={percentage}>
          <SurveyPercentageLabelWrapper>
            <SurveyPercentageLabel>
              {percentage}% ({total})
            </SurveyPercentageLabel>
          </SurveyPercentageLabelWrapper>
        </SurveyPercentage>
      </DataRowWrapper>
    ));
  }, [selectedQuestionAnswer, surveyData]);

  return (
    <Wrapper>
      <HeaderWrapper $isFullWidthEnabled={isFullWidthEnabled}>
        <TotalParticipantsComponent>
          {totalData} {getTranslation('__new.content.liveResults.votes')}
        </TotalParticipantsComponent>

        <QuestionTitleWrapper>
          <QuestionTitleComponent>{selectedQuestion?.Question}</QuestionTitleComponent>
        </QuestionTitleWrapper>
      </HeaderWrapper>

      {isFullWidthEnabled ? (
        <ChildrenMansoryWrapper
          $hasScrollBottom={hasScrollBottom}
          $hasScrollTop={hasScrollTop}
        >
          <MasonryWrapper ref={scrollRef} onScroll={handleScroll}>
            <ResponsiveMasonry>
              <Masonry gutter={theme.size.units(2)}>{getData()}</Masonry>
            </ResponsiveMasonry>
          </MasonryWrapper>
        </ChildrenMansoryWrapper>
      ) : (
        <ScrollWrapper $hasScrollTop={hasScrollTop} $hasScrollBottom={hasScrollBottom}>
          <DataWrapper onScroll={handleScroll} ref={scrollRef}>
            {getData()}
          </DataWrapper>
        </ScrollWrapper>
      )}
    </Wrapper>
  );
};
